import type { ComponentProps } from 'react';
import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';

import { ButtonBase } from 'common/components/Button';
import { Flex } from 'common/components/Flex';
import Separator from 'common/components/Separator';
import { BodyRegularBR1 } from 'common/components/Typography';
import { useRovingTabIndex } from 'common/hooks/useRovingTabIndex';
import { useLocalization } from 'common/providers/LocaleProvider';
import { sanitizeTabLabel } from './utils';

interface TabBarProps {
  tabs: Array<{ label: string }>;
  activeTabIndex: number;
  onTabChange: (label: string, index: number) => void;
}

const StyledTabButton = styled(ButtonBase)<
  ComponentProps<typeof ButtonBase> & { isActive: boolean }
>`
  color: ${({ isActive }) =>
    isActive
      ? themeGet('colors.sys.primary.text.default')
      : themeGet('colors.sys.neutral.text.strong')};
  border-bottom: 3px solid
    ${({ isActive }) => (isActive ? themeGet('colors.sys.primary.border.default') : 'transparent')};

  &:hover {
    border-bottom-color: ${themeGet('colors.sys.primary.border.default')};
  }
`;

const tabBarContainerStyles: ComponentProps<typeof Flex> = {
  justifyContent: 'space-between',
};

const separatorStyles: ComponentProps<typeof Separator> = {
  m: 'sp_0',
};

const TabBar = ({ tabs, activeTabIndex, onTabChange }: TabBarProps) => {
  const { t } = useLocalization();
  const { setFocusedIndex, getTabIndex, getRef, handleKeyDown } = useRovingTabIndex({
    itemCount: tabs.length,
    initialIndex: activeTabIndex,
  });

  useEffect(() => {
    setFocusedIndex(activeTabIndex);
  }, [activeTabIndex, setFocusedIndex]);

  return (
    <Flex flexDirection="column" mb={['sp_16', null, 'sp_0']} px="sp_16">
      <Flex
        {...tabBarContainerStyles}
        role="tablist"
        aria-label={t('h24_side_nav_tabs_aria_label')}
        onKeyDown={handleKeyDown}
      >
        {tabs.map((tab, index) => {
          const isActive = index === activeTabIndex;
          const sanitizedLabel = sanitizeTabLabel(tab.label);

          return (
            <StyledTabButton
              key={`${tab.label}-${index}`}
              type="button"
              role="tab"
              id={`tab-${sanitizedLabel}`}
              aria-selected={isActive}
              aria-controls={`tabpanel-${sanitizedLabel}`}
              isActive={isActive}
              tabIndex={getTabIndex(index)}
              py="sp_12"
              ref={getRef(index)}
              onClick={() => {
                setFocusedIndex(index);
                onTabChange(tab.label, index);
              }}
            >
              <BodyRegularBR1 color="inherit">{tab.label}</BodyRegularBR1>
            </StyledTabButton>
          );
        })}
      </Flex>
      <Separator display={['block', null, 'none']} {...separatorStyles} />
    </Flex>
  );
};

export default TabBar;
